import { useContext, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Table,
  TableBody,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

import { Helmet } from 'react-helmet-async';

import { Button } from 'react-bootstrap';
import { PlanContext } from '../../../../contexts/PlanContext';
import PlanCard from './PlanCard';
import ModalComponent from '../../../../components/ModalComponent';
import Plan from '../../../../models/Plan/Plan';
import PlanConfirm from './PlanConfirm';
import FormStyle from '../FormStyle';
import showTableData from '../Functions/showTableData';
import StatusStyle from './StatusStyle';
import { PagesContext } from '../../../../contexts/PagesContext';
import { useUserContext } from '../../../../contexts/UserContext';
import planAPI from '../../../../services/plan';
import config from '../../../../config';
import TwoFactorAuthModal from '../../../../components/Modals/TwoFactorAuthModal';
import api from '../../../../services/api';

function Index() {
  // variables
  const Navigate = useNavigate();
  const { totalPublishedPages, List } = useContext(PagesContext);
  const { plans, hasPaymentMethod, requestPlansList } = useContext(PlanContext);
  const { user, logoutUser } = useUserContext();

  const [triedToRegisterToPlan, setTriedToRegisterToPlan] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);
  const [showSinglePagePlanModal, setShowSinglePagePlanModal] = useState(false);
  const [currentNumberOfSinglePages, setCurrentNumberOfSinglePages] = useState(0);
  const [desiredNumberOfSinglePages, setDesiredNumberOfSinglePages] = useState(0);
  const [isSubmittingSinglePage, setIsSubmittingSinglePage] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Check if browser language is Japanese
  const isJapanese = () => {
    const browserLanguage = navigator.language || (navigator as any).userLanguage;
    return browserLanguage.toLowerCase().startsWith('ja');
  };

  // functions
  const getInitialData = () => {
    setIsLoading(true);
    async function awaitCurrentTotalPages() {
      List();
    }

    async function awaitrequestPlansList(setIsLoading: (isLoading: boolean) => void) {
      await awaitCurrentTotalPages();
      await requestPlansList((isLoading) => setIsLoading(isLoading));
    }

    awaitrequestPlansList(setIsLoading)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSinglePageQuantityChange = (newQuantity: number) => {
    // Get the current active plan's page limit
    const currentPlanPages = activePlan ? (parseInt(activePlan.metadata?.feat_pages, 10) || 0) : 0;

    // Calculate the new total number of single pages
    const newDesiredTotal = desiredNumberOfSinglePages + newQuantity;

    // Check if the new total would be less than what's needed
    // Ensure the new total number of pages (current plan pages + desired single pages)
    // does not fall below the total number of pages required (`localTotal`).
    // This validation prevents users from reducing their single-page count
    // to a level that violates the minimum page requirements.
    if ((currentPlanPages + newDesiredTotal) < localTotal) {
      return; // Don't allow the change
    }

    // Minimum is 0 if already have single pages, 1 if adding for first time
    const minimumAllowedAmount = (selectedPlan?.active ? 0 : 1);

    // Ensure we don't go below the minimum
    const finalDesiredPages = Math.max(minimumAllowedAmount, newDesiredTotal);

    setDesiredNumberOfSinglePages(finalDesiredPages);
  };

  const handleCloseChosenPlanModal = () => {
    setSelectedPlan(null);
    setTriedToRegisterToPlan(false);
  };

  const handleConfirmModal = () => {
    Navigate('/my-account/payment-methods');
    setTriedToRegisterToPlan(false);
  };

  const getMinimumRequiredPages = () => {
    const currentPlanPages = activePlan ? (parseInt(activePlan.metadata?.feat_pages, 10) || 0) : 0;
    return Math.max(0, localTotal - currentPlanPages);
  };

  const singlePagePlan = (plan: Plan) => {
    setSelectedPlan(plan);
    if (plan.metadata.type === 'singlePage') {
      setDesiredNumberOfSinglePages(plan?.quantity_of_acquired_single_pages || 1);
      if (!hasPaymentMethod) {
        setTriedToRegisterToPlan(true);
      } else {
        setShowSinglePagePlanModal(true);
      }
    } else {
      setTriedToRegisterToPlan(true);
    }
  };

  const submitSinglePagesToBuy = async (desiredNumberOfSinglePages: number) => {
    if (isSubmittingSinglePage) return;

    setIsSubmittingSinglePage(true);
    try {
      await planAPI.postDesiredSinglePages({
        desired_number_of_single_pages: desiredNumberOfSinglePages,
      });
      setShowSinglePagePlanModal(false);
      setShowSuccessModal(true);
      // Add this to force refresh plans list
      await requestPlansList((loading) => setIsLoading(loading));
      // Reset current number of single pages if it's 0
      if (desiredNumberOfSinglePages === 0) {
        setCurrentNumberOfSinglePages(0);
      }
    } catch (error) {
      setShowSinglePagePlanModal(false);
      setShowErrorModal(true);
    } finally {
      setIsSubmittingSinglePage(false);
      getInitialData();
    }
  };

  // 2FA functions for billing portal
  const handleSendTwoFactorCode = async (resend = false) => {
    try {
      await api.post('/v1/payment-methods/send-two-factor-code', { resend });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || '認証コードの送信に失敗しました';
      setTwoFactorError(errorMessage);
      throw error;
    }
  };

  const handleOpenBillingPortal = async () => {
    setIsAccountLocked(false);
    setTwoFactorError('');
    setShowTwoFactorModal(true);
    await handleSendTwoFactorCode().catch(() => {});
  };

  const handleVerifyTwoFactor = async (code: string) => {
    setTwoFactorLoading(true);
    setTwoFactorError('');

    try {
      const response = await api.post('/v1/plans/billing-portal', {
        code: code.trim(),
      });
      setShowTwoFactorModal(false);
      window.location.href = response.data.url;
    } catch (error: any) {
      const isAccountLockedError = error?.response?.data?.is_account_locked ?? false;
      if (isAccountLockedError) {
        setIsAccountLocked(true);
        setTwoFactorError('');
        setTwoFactorLoading(false);
        setTimeout(() => {
          logoutUser();
        }, 2000);
        return;
      }

      if (error?.response?.data?.message) {
        const errorMsg = error.response.data.message;
        if (errorMsg.includes('ログイン試行回数の上限に達したため、アカウントはロックされました。')) {
          setIsAccountLocked(true);
          setTwoFactorError('');
          setTimeout(() => {
            logoutUser();
          }, 2000);
        } else {
          setTwoFactorError(errorMsg);
        }
      } else {
        setTwoFactorError('認証コードが正しくありません');
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // effects
  // fetch plans whenever the page is loaded or the user changes the plan
  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalTotal(totalPublishedPages);
  }, [totalPublishedPages]);

  useEffect(() => {
    // Find the single page plan and set the current number of single pages
    const singlePagePlan = plans.find((plan) => plan.name === 'ページ追加' || plan.metadata?.type === 'singlePage');
    if (singlePagePlan) {
      setCurrentNumberOfSinglePages(singlePagePlan.quantity_of_acquired_single_pages || 0);
    }
  }, [plans]);

  // render conditions
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="90vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (hasPaymentMethod && selectedPlan && selectedPlan.metadata.type !== 'singlePage') {
    return (
      <PlanConfirm
        plan={selectedPlan}
        oldPlanName={activePlan?.name}
        setSelectedPlan={setSelectedPlan}
        requestPlansList={requestPlansList}
      />
    );
  }

  return (
    <div className="main">
      <Helmet>
        <title>プラン情報</title>
      </Helmet>

      <main
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 65px)',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          className="container-fluid p-0"
          sx={{
            height: 'calc(100vh - 90px)',
          }}
        >
          <Box sx={FormStyle.previewHeader}>
            <h1 className="text-bold">プラン情報</h1>
            <p>プラン情報の確認・変更はこちら</p>
          </Box>

          <Box>
            <Box className="container-fluid my-2">

              <Grid container spacing={1}>
                {plans.map((plan) => {
                  if (plan.name === 'ページ追加' && user.myPlan.name === 'フリープラン') {
                    return null;
                  }

                  if (plan.name === 'ページ追加' && user.is_invoice_mode_enabled) {
                    return null;
                  }

                  // Hide business plan when browser language is NOT Japanese
                  if (plan.name === 'ビジネスプラン' && !isJapanese()) {
                    return null;
                  }

                  return (
                    <Grid
                      item
                      key={plan.id}
                      xs={12}
                      sm={6}
                      lg={3}
                      xl={user.is_invoice_mode_enabled ? 2.40 : 3}
                    >
                      <PlanCard
                        plan={plan}
                        total={localTotal}
                        setTriedToRegisterToPlan={setTriedToRegisterToPlan}
                        setSelectedPlan={setSelectedPlan}
                        setActivePlan={setActivePlan}
                        singlePagesCount={currentNumberOfSinglePages}
                        customOnClickEventFor={(plan) => { singlePagePlan(plan); }}
                      />
                    </Grid>
                  );
                })}
                <Grid
                  item
                  xs={12}
                  sm
                  lg
                  xl
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      ...StatusStyle.isNotLowerPlan,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      className="card-body"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        flex: 1,
                        minHeight: 75,
                      }}
                    >
                      <Button
                        className="btn btn-danger w-100"
                        style={{ fontSize: 16, padding: '12px', maxWidth: '500px' }}
                        onClick={() => {
                          const url = config.plan.informationUrl;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        各プランを確認する
                      </Button>
                      <Button
                        className="btn btn-danger w-100"
                        style={{ fontSize: 16, padding: '12px', maxWidth: '500px' }}
                        onClick={handleOpenBillingPortal}
                      >
                        支払履歴・キャンセルを確認する
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Box className="card my-3">
                <Box className="card-body">
                  <h4 className="text-bold mb-3">お支払方法</h4>
                  <Table className="table-form-setting">
                    <TableBody>
                      {
                        showTableData({
                          ticket: (hasPaymentMethod ? '支払方法設定済み' : '支払方法未設定'),
                          ticketBackgroundStyle: (hasPaymentMethod ? StatusStyle.cardRegistered : StatusStyle.cardNotRegistered),
                          value: (
                            <Link href="/my-account/payment-methods">
                              {hasPaymentMethod ? '>お支払方法を確認する' : '>お支払方法を登録する'}
                            </Link>
                          ),
                        })
                      }
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </main>

      {/* modals */}
      <ModalComponent
        title="お支払方法未登録"
        confirmButtonText="お支払方法へ"
        cancelButtonText="閉じる"
        onConfirm={handleConfirmModal}
        show={triedToRegisterToPlan && !hasPaymentMethod}
        closeModal={handleCloseChosenPlanModal}
      >
        <Box className="text-danger my-2">
          <FontAwesomeIcon icon={faCircleExclamation} />
          お支払方法が登録されておりません。
        </Box>
        <Box className="my-2">「お支払方法」のページよりご登録をお願いいたします。 </Box>
      </ModalComponent>

      <ModalComponent
        title="ページ追加"
        confirmButtonText="適用する"
        cancelButtonText="キャンセル"
        backdrop="static"
        onConfirm={async () => {
          await submitSinglePagesToBuy(desiredNumberOfSinglePages);
        }}
        show={showSinglePagePlanModal}
        closeModal={() => { setShowSinglePagePlanModal(false); }}
        footerClass="d-flex justify-content-between"
        loading={isSubmittingSinglePage}
        disableConfirm={desiredNumberOfSinglePages === currentNumberOfSinglePages}
      >

        <Box
          className="my-2 d-flex pt-2 pb-3"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ fontWeight: 'bold', fontSize: 20 }}
        >
          追加１ページ 有料
        </Box>
        <Box
          className="my-2 d-flex py-2"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ fontSize: 24 }}
        >
          <Box sx={{
            fontWeight: 'bold', fontSize: 20, mr: 2, textAlign: 'center',
          }}
          >
            ページ数
          </Box>
          <Button
            onClick={() => handleSinglePageQuantityChange(-1)}
            style={{ fontSize: 12, minWidth: 40 }}
          >
            -
          </Button>
          <Box sx={{
            fontWeight: 'bold', fontSize: 20, mx: 0.5, px: 2, textAlign: 'center', border: '1px solid #ccc',
          }}
          >
            {desiredNumberOfSinglePages}
          </Box>
          <Button
            onClick={() => handleSinglePageQuantityChange(1)}
            style={{ fontSize: 12, minWidth: 40 }}
          >
            +
          </Button>
        </Box>
        {desiredNumberOfSinglePages === currentNumberOfSinglePages && (
          <Box className="pt-3 text-danger text-center" style={{ fontSize: 14 }}>
            現在適用中のページ数と同じ数が選択されています。変更はありません。
          </Box>
        )}
        {getMinimumRequiredPages() > 0 && (
          <Box className="pt-3 text-info text-center" style={{ fontSize: 14 }}>
            現在のプランのページ数と現在公開中のページ数による
            <br />
            最低の選択可能ページ数は
            {getMinimumRequiredPages()}
            ページです。
          </Box>
        )}
        {(selectedPlan?.active && desiredNumberOfSinglePages < 1)
          && (
            <Box className="pt-3 text-danger text-center">
              ※ ゼロページで適用すると全ての追加ページをキャンセルされます。
            </Box>
          )}

      </ModalComponent>
      <ModalComponent
        title="ページ追加完了"
        confirmButtonText="閉じる"
        show={showSuccessModal}
        hideCancelButton
        closeModal={() => { }}
        onConfirm={() => setShowSuccessModal(false)}
      >
        <Box>
          ページの追加が正常に完了しました。
        </Box>
      </ModalComponent>

      <ModalComponent
        title="ページ追加エラー"
        confirmButtonText="閉じる"
        show={showErrorModal}
        onConfirm={() => setShowErrorModal(false)}
        closeModal={() => setShowErrorModal(false)}
      >
        <Box className="text-danger my-2">
          ページの追加中にエラーが発生しました。しばらくしてから再度お試しください。
        </Box>
      </ModalComponent>

      <TwoFactorAuthModal
        show={showTwoFactorModal}
        onVerify={handleVerifyTwoFactor}
        onResendCode={() => handleSendTwoFactorCode(true)}
        onClose={() => {
          setShowTwoFactorModal(false);
          setIsAccountLocked(false);
          setTwoFactorError('');
        }}
        loading={twoFactorLoading}
        errorMessage={twoFactorError}
        isAccountLocked={isAccountLocked}
      />

    </div>
  );
}

export default Index;
